// =============================================================================
// TeamPage — Team member management (invite, list, roles)
// =============================================================================

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Modal,
  ModalFooter,
  Input,
  Select,
} from '@tkpod/ui';
import { hasAnyPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MemberUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar?: string | null;
}

interface MemberRole {
  id: string;
  name: string;
  slug: string;
}

interface Member {
  id: string;
  handle: string;
  user: MemberUser;
  role: MemberRole;
  isActive: boolean;
  invitedAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

interface MembersResponse {
  data: Member[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

interface RolesResponse {
  data: MemberRole[];
}

interface InvitePayload {
  email: string;
  roleSlug: string;
}

interface InviteResponse {
  data: {
    email: string;
    roleSlug: string;
    organizationName: string | null;
    inviteLink: string;
    inviteToken: string;
    expiresAt: string;
    expiresInHours: number;
  };
}

interface PendingInvitation {
  id: string;
  email: string;
  roleSlug: string;
  status: 'PENDING';
  invitedBy: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  createdAt: string;
  expiresAt: string;
}

interface InvitationsResponse {
  data: PendingInvitation[];
}

interface ChangeRolePayload {
  memberId: string;
  roleSlug: string;
}

// ---------------------------------------------------------------------------
// RBAC helpers
// ---------------------------------------------------------------------------

const ROLE_HIERARCHY: Record<string, number> = {
  owner: 4,
  admin: 3,
  manager: 2,
  designer: 1,
};

function canInvite(permissions: string[]): boolean {
  return hasAnyPermission(permissions, ['member:invite', 'member:manage']);
}

function canSuspendOrRemove(permissions: string[]): boolean {
  return hasAnyPermission(permissions, ['member:remove_member', 'member:manage']);
}

function canChangeRole(permissions: string[]): boolean {
  return hasAnyPermission(permissions, ['member:change_role', 'member:manage']);
}

function canManageMember(
  callerRole: string | undefined,
  targetRole: string,
): boolean {
  if (!callerRole) return false;
  return (ROLE_HIERARCHY[callerRole] ?? 0) > (ROLE_HIERARCHY[targetRole] ?? 0);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(member: Member): string {
  const parts = [member.user.firstName, member.user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : member.user.email;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const queryKeys = {
  members: (orgId: string | undefined) => ['organizations', orgId, 'members'] as const,
  roles: (orgId: string | undefined) => ['organizations', orgId, 'roles'] as const,
  invitations: (orgId: string | undefined) => ['organizations', orgId, 'invitations'] as const,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TeamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const org = user?.currentOrganization;
  const orgId = org?.id;
  const callerRole = org?.role;
  const permissions = org?.permissions ?? [];

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<InviteResponse['data'] | null>(null);

  const [linkCopied, setLinkCopied] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<Member | null>(null);

  const [roleChangeTarget, setRoleChangeTarget] = useState<Member | null>(null);
  const [newRoleSlug, setNewRoleSlug] = useState('');

  const [revokeTarget, setRevokeTarget] = useState<PendingInvitation | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'suspended'>('');

  const [membersPage, setMembersPage] = useState(1);
  const [membersPageSize, setMembersPageSize] = useState(25);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(25);

  // --- Queries ---

  const membersQuery = useQuery<MembersResponse, Error>({
    queryKey: queryKeys.members(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID missing');
      return api.get<MembersResponse>(`/api/v1/organizations/${orgId}/members?limit=250`);
    },
    enabled: !!orgId,
  });

  const rolesQuery = useQuery<RolesResponse, Error>({
    queryKey: queryKeys.roles(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID missing');
      return api.get<RolesResponse>(`/api/v1/organizations/${orgId}/roles`);
    },
    enabled: !!orgId && canInvite(permissions),
  });

  const invitationsQuery = useQuery<InvitationsResponse, Error>({
    queryKey: queryKeys.invitations(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID missing');
      return api.get<InvitationsResponse>(`/api/v1/organizations/${orgId}/invitations`);
    },
    enabled: !!orgId && canInvite(permissions),
  });

  // --- Mutations ---

  const inviteMutation = useMutation<InviteResponse, Error, InvitePayload>({
    mutationFn: async (data) => {
      if (!orgId) throw new Error('Organization ID missing');
      return api.post<InviteResponse>(`/api/v1/organizations/${orgId}/members/invite`, data);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.invitations(orgId) });
      setInviteSuccess(result.data);
      setInviteEmail('');
      setInviteRole('');
      setInviteError(null);
    },
    onError: (err) => {
      setInviteError(err.message || 'Failed to send invitation.');
    },
  });

  const changeRoleMutation = useMutation<void, Error, ChangeRolePayload>({
    mutationFn: async (data) => {
      if (!orgId) throw new Error('Organization ID missing');
      await api.patch(`/api/v1/organizations/${orgId}/members/${data.memberId}`, {
        roleSlug: data.roleSlug,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      setRoleChangeTarget(null);
      setNewRoleSlug('');
    },
  });

  const removeMutation = useMutation<void, Error, string>({
    mutationFn: async (memberId) => {
      if (!orgId) throw new Error('Organization ID missing');
      await api.delete(`/api/v1/organizations/${orgId}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      setRemoveTarget(null);
    },
  });

  const revokeInviteMutation = useMutation<void, Error, string>({
    mutationFn: async (invitationId) => {
      if (!orgId) throw new Error('Organization ID missing');
      setRevokingId(invitationId);
      await api.delete(`/api/v1/organizations/${orgId}/invitations/${invitationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invitations(orgId) });
    },
    onSettled: () => {
      setRevokingId(null);
    },
  });

  const suspendMutation = useMutation<void, Error, string>({
    mutationFn: async (memberId) => {
      if (!orgId) throw new Error('Organization ID missing');
      await api.patch(`/api/v1/organizations/${orgId}/members/${memberId}/suspend`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      setSuspendTarget(null);
    },
  });

  const reactivateMutation = useMutation<void, Error, string>({
    mutationFn: async (memberId) => {
      if (!orgId) throw new Error('Organization ID missing');
      await api.patch(`/api/v1/organizations/${orgId}/members/${memberId}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.members(orgId) });
    },
  });

  // --- Helpers ---

  const closeInviteModal = useCallback(() => {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteRole('');
    setInviteError(null);
    setInviteSuccess(null);
    inviteMutation.reset();
  }, [inviteMutation]);

  const handleInviteSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setInviteError(null);
      const email = inviteEmail.trim();
      if (!email) {
        setInviteError('Email is required.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setInviteError('Please enter a valid email address.');
        return;
      }
      if (!inviteRole) {
        setInviteError('Please select a role.');
        return;
      }
      inviteMutation.mutate({ email, roleSlug: inviteRole });
    },
    [inviteEmail, inviteRole, inviteMutation],
  );

  const handleRoleChangeSubmit = useCallback(() => {
    if (!roleChangeTarget || !newRoleSlug) return;
    changeRoleMutation.mutate({
      memberId: roleChangeTarget.id,
      roleSlug: newRoleSlug,
    });
  }, [roleChangeTarget, newRoleSlug, changeRoleMutation]);

  const assignableRoleOptions = useMemo(() => {
    const roles = rolesQuery.data?.data ?? [];
    return roles
      .filter((r) => canManageMember(callerRole, r.slug))
      .map((r) => ({ value: r.slug, label: r.name }));
  }, [rolesQuery.data, callerRole]);

  const handleCopyLink = useCallback(async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  }, []);

  // --- Filtered & paginated members ---

  const allMembers = membersQuery.data?.data ?? [];

  const availableRoles = useMemo(() => {
    const map = new Map<string, string>();
    allMembers.forEach((m) => map.set(m.role.slug, m.role.name));
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [allMembers]);

  const filteredMembers = useMemo(() => {
    let result = allMembers;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.user.email.toLowerCase().includes(q) ||
          (m.user.firstName?.toLowerCase().includes(q) ?? false) ||
          (m.user.lastName?.toLowerCase().includes(q) ?? false) ||
          m.role.name.toLowerCase().includes(q),
      );
    }
    if (roleFilter) {
      result = result.filter((m) => m.role.slug === roleFilter);
    }
    if (statusFilter === 'active') {
      result = result.filter((m) => m.isActive);
    } else if (statusFilter === 'suspended') {
      result = result.filter((m) => !m.isActive);
    }
    return result;
  }, [allMembers, search, roleFilter, statusFilter]);

  const paginatedMembers = useMemo(() => {
    const start = (membersPage - 1) * membersPageSize;
    return filteredMembers.slice(start, start + membersPageSize);
  }, [filteredMembers, membersPage, membersPageSize]);

  const membersTotalPages = Math.max(1, Math.ceil(filteredMembers.length / membersPageSize));

  const pendingInvitations = invitationsQuery.data?.data ?? [];

  const paginatedInvitations = useMemo(() => {
    const start = (invPage - 1) * invPageSize;
    return pendingInvitations.slice(start, start + invPageSize);
  }, [pendingInvitations, invPage, invPageSize]);

  const invTotalPages = Math.max(1, Math.ceil(pendingInvitations.length / invPageSize));

  const hasAnyMemberAction = canChangeRole(permissions) || canSuspendOrRemove(permissions);

  // --- Render ---

  if (!org) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        No organization found. Please contact support.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ---- Team Members ---- */}
      <Card padding="none">
        <div className="p-6 pb-0">
          <CardHeader
            title="Team Members"
            description={`Manage team members for ${org.name}.`}
            action={
              canInvite(permissions) ? (
                <Button size="sm" onClick={() => setInviteOpen(true)}>
                  + Invite Member
                </Button>
              ) : undefined
            }
          />
        </div>

        {/* Filters row */}
        <div className="mt-4 px-6 flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder="Search by name, email, or role..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setMembersPage(1);
              }}
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setMembersPage(1);
            }}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
          >
            <option value="">All Roles</option>
            {availableRoles.map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as '' | 'active' | 'suspended');
              setMembersPage(1);
            }}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>

          {(search || roleFilter || statusFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setRoleFilter('');
                setStatusFilter('');
                setMembersPage(1);
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Members table */}
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Last Login
                </th>
                {hasAnyMemberAction && (
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface">
              {membersQuery.isLoading ? (
                <tr>
                  <td colSpan={hasAnyMemberAction ? 6 : 5} className="px-4 py-12 text-center text-sm text-text-tertiary">
                    <div className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : paginatedMembers.length === 0 ? (
                <tr>
                  <td colSpan={hasAnyMemberAction ? 6 : 5} className="px-4 py-12 text-center text-sm text-text-tertiary">
                    {search || roleFilter || statusFilter
                      ? 'No members match your filters.'
                      : 'No team members found. Invite your first member to get started.'}
                  </td>
                </tr>
              ) : (
                paginatedMembers.map((member) => (
                  <tr
                    key={member.id}
                    className="transition-colors hover:bg-surface-secondary"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => navigate(`/settings/team/${member.handle}`, { state: { memberId: member.id } })}
                        className="text-sm font-medium text-brand-400 hover:text-brand-300 hover:underline"
                      >
                        {displayName(member)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {member.user.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {member.role.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {member.isActive ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-success-light px-2 py-0.5 text-xs font-medium text-success-dark">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-light px-2 py-0.5 text-xs font-medium text-danger-dark">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-danger" />
                          Suspended
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                      {member.lastLoginAt
                        ? formatDate(member.lastLoginAt)
                        : 'Never'}
                    </td>
                    {hasAnyMemberAction && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => navigate(`/settings/team/${member.handle}`, { state: { memberId: member.id } })}
                            className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors"
                          >
                            View
                          </button>
                          {member.user.id !== user?.id && canManageMember(callerRole, member.role.slug) && (
                            <>
                            {canChangeRole(permissions) && (
                              <button
                                type="button"
                                onClick={() => {
                                  setRoleChangeTarget(member);
                                  setNewRoleSlug(member.role.slug);
                                }}
                                className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors"
                              >
                                Change Role
                              </button>
                            )}
                            {canSuspendOrRemove(permissions) &&
                              (member.isActive ? (
                                <button
                                  type="button"
                                  onClick={() => setSuspendTarget(member)}
                                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 hover:bg-surface-secondary transition-colors"
                                >
                                  Suspend
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    reactivateMutation.mutate(member.id)
                                  }
                                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-green-400 hover:text-green-300 hover:bg-surface-secondary transition-colors"
                                >
                                  Activate
                                </button>
                              ))}
                            {canSuspendOrRemove(permissions) && (
                              <button
                                type="button"
                                onClick={() => setRemoveTarget(member)}
                                className="rounded-lg px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-surface-secondary transition-colors"
                              >
                                Remove
                              </button>
                            )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!membersQuery.isLoading && filteredMembers.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <span className="text-xs text-text-tertiary">
              Showing{' '}
              {Math.min((membersPage - 1) * membersPageSize + 1, filteredMembers.length)}
              –{Math.min(membersPage * membersPageSize, filteredMembers.length)}
              {' '}of {filteredMembers.length}
              {filteredMembers.length !== allMembers.length ? ` (filtered from ${allMembers.length})` : ''} members
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-tertiary">Show</span>
                <select
                  value={membersPageSize}
                  onChange={(e) => {
                    setMembersPageSize(Number(e.target.value));
                    setMembersPage(1);
                  }}
                  className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              {membersTotalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setMembersPage((p) => Math.max(1, p - 1))}
                    disabled={membersPage === 1}
                    className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ‹ Prev
                  </button>
                  <span className="text-xs text-text-tertiary">
                    {membersPage} / {membersTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setMembersPage((p) => Math.min(membersTotalPages, p + 1))}
                    disabled={membersPage === membersTotalPages}
                    className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {membersQuery.isError && (
          <div className="mx-6 mb-6 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
            Failed to load team members: {membersQuery.error.message}
          </div>
        )}
      </Card>

      {/* ---- Pending Invitations ---- */}
      {canInvite(permissions) && pendingInvitations.length > 0 && (
        <Card padding="none">
          <div className="p-6 pb-0">
            <CardHeader
              title="Pending Invitations"
              description="Invitations that have not yet been accepted."
            />
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full table-fixed divide-y divide-border">
              <colgroup>
                <col style={{ width: '35%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Invited As</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Invited By</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Expires</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {invitationsQuery.isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-text-tertiary">
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : pendingInvitations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-text-tertiary">
                      No pending invitations.
                    </td>
                  </tr>
                ) : (
                  paginatedInvitations.map((row) => (
                    <tr key={row.id} className="transition-colors duration-100">
                      <td className="px-4 py-3 text-sm font-medium text-text-primary whitespace-nowrap truncate">
                        {row.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary whitespace-nowrap">
                        <Badge variant="default" className="capitalize">{row.roleSlug}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary whitespace-nowrap">
                        <Badge variant="warning" dot>Pending</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap truncate">
                        {row.invitedBy.firstName ?? row.invitedBy.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                        {new Date(row.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const link = `${window.location.origin}/join/${row.id}`;
                              try {
                                await navigator.clipboard.writeText(link);
                                setCopiedInviteId(row.id);
                                setTimeout(() => setCopiedInviteId(null), 2000);
                              } catch { /* clipboard not available */ }
                            }}
                          >
                            {copiedInviteId === row.id ? 'Copied!' : 'Copy Link'}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setRevokeTarget(row)}
                            loading={revokingId === row.id}
                          >
                            Revoke
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pendingInvitations.length > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-xs text-text-tertiary">
                Showing{' '}
                {Math.min((invPage - 1) * invPageSize + 1, pendingInvitations.length)}
                –{Math.min(invPage * invPageSize, pendingInvitations.length)}
                {' '}of {pendingInvitations.length} invitations
              </span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-tertiary">Show</span>
                  <select
                    value={invPageSize}
                    onChange={(e) => {
                      setInvPageSize(Number(e.target.value));
                      setInvPage(1);
                    }}
                    className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                {invTotalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setInvPage((p) => Math.max(1, p - 1))}
                      disabled={invPage === 1}
                      className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ‹ Prev
                    </button>
                    <span className="text-xs text-text-tertiary">
                      {invPage} / {invTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setInvPage((p) => Math.min(invTotalPages, p + 1))}
                      disabled={invPage === invTotalPages}
                      className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next ›
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ---- Invite Modal ---- */}
      <Modal
        open={inviteOpen}
        onClose={closeInviteModal}
        title="Invite Team Member"
        description="Send an invitation to join your organization."
        size="sm"
      >
        {inviteSuccess ? (
          <div>
            <div className="mb-4 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
              Invitation created for <strong>{inviteSuccess.email}</strong> as{' '}
              <strong>{inviteSuccess.roleSlug}</strong>.
            </div>

            <div className="space-y-3">
              <p className="text-sm text-text-secondary">
                Share this invite link with the user. It expires in{' '}
                {inviteSuccess.expiresInHours} hours.
              </p>

              <div className="rounded-lg border border-border bg-surface-secondary p-3">
                <label className="mb-1 block text-xs font-medium text-text-tertiary">
                  Invite Link
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded-md bg-surface-primary px-3 py-2 text-xs text-text-primary border border-border">
                    {inviteSuccess.inviteLink}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleCopyLink(inviteSuccess.inviteLink)}
                  >
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-text-tertiary">
                When email notifications are enabled, invitations will also be sent via email automatically.
              </p>
            </div>

            <ModalFooter>
              <Button onClick={closeInviteModal}>Done</Button>
            </ModalFooter>
          </div>
        ) : (
          <form onSubmit={handleInviteSubmit}>
            {inviteError && (
              <div className="mb-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {inviteError}
              </div>
            )}

            <div className="space-y-4">
              <Input
                label="Email Address"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
                required
              />

              <Select
                label="Role"
                options={assignableRoleOptions}
                value={inviteRole}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setInviteRole(e.target.value)
                }
                placeholder="Select a role"
              />
            </div>

            <ModalFooter>
              <Button variant="outline" type="button" onClick={closeInviteModal}>
                Cancel
              </Button>
              <Button type="submit" loading={inviteMutation.isPending}>
                Send Invitation
              </Button>
            </ModalFooter>
          </form>
        )}
      </Modal>

      {/* ---- Change Role Modal ---- */}
      <Modal
        open={!!roleChangeTarget}
        onClose={() => {
          setRoleChangeTarget(null);
          setNewRoleSlug('');
        }}
        title="Change Member Role"
        description={
          roleChangeTarget
            ? `Update role for ${displayName(roleChangeTarget)}.`
            : undefined
        }
        size="sm"
      >
        <div className="space-y-4">
          <Select
            label="New Role"
            options={assignableRoleOptions}
            value={newRoleSlug}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setNewRoleSlug(e.target.value)
            }
          />
        </div>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setRoleChangeTarget(null);
              setNewRoleSlug('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRoleChangeSubmit}
            loading={changeRoleMutation.isPending}
            disabled={
              !newRoleSlug || newRoleSlug === roleChangeTarget?.role.slug
            }
          >
            Update Role
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---- Suspend Confirm Modal ---- */}
      <Modal
        open={!!suspendTarget}
        onClose={() => setSuspendTarget(null)}
        title="Suspend Team Member"
        description={
          suspendTarget
            ? `Are you sure you want to suspend ${displayName(suspendTarget)}? They will lose access to the organization until reactivated.`
            : undefined
        }
        size="sm"
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setSuspendTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (suspendTarget) suspendMutation.mutate(suspendTarget.id);
            }}
            loading={suspendMutation.isPending}
          >
            Suspend Member
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---- Revoke Invite Confirm Modal ---- */}
      <Modal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke Invitation"
        description={
          revokeTarget
            ? `Are you sure you want to revoke the invitation for ${revokeTarget.email}? This action cannot be undone.`
            : undefined
        }
        size="sm"
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setRevokeTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (revokeTarget) {
                revokeInviteMutation.mutate(revokeTarget.id);
                setRevokeTarget(null);
              }
            }}
            loading={revokeInviteMutation.isPending}
          >
            Revoke
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---- Remove Confirm Modal ---- */}
      <Modal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title="Remove Team Member"
        description={
          removeTarget
            ? `Are you sure you want to remove ${displayName(removeTarget)} from the organization? This action cannot be undone.`
            : undefined
        }
        size="sm"
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (removeTarget) removeMutation.mutate(removeTarget.id);
            }}
            loading={removeMutation.isPending}
          >
            Remove Member
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
